import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  FolderKanban, Inbox, FileText, LifeBuoy,
  LogOut, Settings, Sun, Moon, Monitor, ChevronRight, Menu, X, LayoutDashboard
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { safeLocalStorage } from '@/lib/storage-utils';

export const portalNavItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'projects',  label: 'Projects',  icon: FolderKanban, path: '/projects' },
  { id: 'inbox',     label: 'Inbox',     icon: Inbox,        path: '/inbox' },
  { id: 'proposals', label: 'Proposals', icon: FileText,      path: '/proposals' },
  { id: 'support',   label: 'Support',   icon: LifeBuoy,     path: '/support' },
];

interface PortalLayoutProps {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (v: boolean) => void;
  children?: React.ReactNode;
}

export const PortalLayout: React.FC<PortalLayoutProps> = ({ isGlobalDarkMode, setIsGlobalDarkMode, children }) => {
  const { profile, signOut } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Close profile menu on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node) &&
        profileButtonRef.current && !profileButtonRef.current.contains(e.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showProfileMenu]);

  const displayName = profile?.first_name
    ? `${profile.first_name} ${profile.last_name || ''}`.trim()
    : profile?.email?.split('@')[0] || 'Client';

  const initials = (profile?.first_name?.[0] || profile?.email?.[0] || 'C').toUpperCase();

  const workspaceLogo = currentWorkspace?.appearance?.logo || currentWorkspace?.logo || currentWorkspace?.appearance?.favicon || currentWorkspace?.favicon;

  // Nav link builder used for both desktop & mobile
  const buildNavLinks = (onNavigate?: () => void) =>
    portalNavItems.map(item => {
      const Icon = item.icon;
      const isActive = location.pathname.startsWith(item.path);
      return (
        <button
          key={item.id}
          onClick={() => { navigate(item.path); onNavigate?.(); }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            isActive
              ? isGlobalDarkMode
                ? 'bg-nav-active text-white shadow-md'
                : 'bg-sidebar-light-active text-gray-900'
              : isGlobalDarkMode
                ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover'
          }`}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span>{item.label}</span>
        </button>
      );
    });

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${isGlobalDarkMode ? 'bg-background' : 'bg-gray-50'}`}>

      {/* ───── Mobile Top Bar ───── */}
      <div className={`md:hidden flex items-center justify-between p-4 border-b sticky top-0 z-50 ${
        isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-sidebar-light border-gray-200'
      }`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center flex-shrink-0">
            {workspaceLogo ? (
              <img src={workspaceLogo} alt="Logo" className="h-7 w-auto object-contain max-w-[120px]" />
            ) : (
              <img src={isGlobalDarkMode ? '/icon-white.svg' : '/icon-blue.svg'} alt="App Icon" className="h-7 w-7 object-contain" />
            )}
          </div>
          <span className={`font-semibold ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900'}`}>
            Client Portal
          </span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`p-2 rounded-lg transition-colors ${isGlobalDarkMode ? 'text-sidebar-foreground/70 hover:bg-sidebar-accent' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* ───── Mobile Menu ───── */}
      {mobileMenuOpen && (
        <div className={`md:hidden fixed inset-0 top-[65px] z-40 overflow-y-auto ${
          isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'
        }`}>
          <div className="p-4 space-y-1">
            {buildNavLinks(() => setMobileMenuOpen(false))}
            <div className={`pt-4 mt-4 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
              <button
                onClick={() => { navigate('?modal=settings'); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isGlobalDarkMode ? 'text-sidebar-foreground/80 hover:bg-sidebar-accent' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                Account Settings
              </button>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── Desktop Sidebar ───── */}
      <aside className={`hidden md:flex w-56 flex-col h-screen sticky top-0 left-0 border-r transition-colors duration-300 z-40 ${
        isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-sidebar-light border-gray-200'
      }`}>

        {/* Workspace Header */}
        <div className={`p-5 border-b flex items-center gap-3 ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
          <div className="flex items-center justify-center flex-shrink-0">
            {workspaceLogo ? (
              <img src={workspaceLogo} alt="Logo" className="h-8 w-auto object-contain max-w-[160px]" />
            ) : (
              <img src={isGlobalDarkMode ? '/icon-white.svg' : '/icon-blue.svg'} alt="App Icon" className="h-8 w-8 object-contain" />
            )}
          </div>
          <h2 className={`text-sm font-semibold truncate ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900'}`}>
            {currentWorkspace?.name}
          </h2>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {buildNavLinks()}
        </nav>

        {/* Bottom — Profile */}
        <div className={`p-3 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
          <div className="relative">
            <button
              ref={profileButtonRef}
              onClick={() => setShowProfileMenu(v => !v)}
              className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200 ${
                showProfileMenu
                  ? isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-sidebar-light-hover'
                  : isGlobalDarkMode ? 'hover:bg-sidebar-accent' : 'hover:bg-sidebar-light-hover'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-200'}`}>
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className={`text-sm font-semibold ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-700'}`}>{initials}</span>
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className={`text-sm font-medium truncate ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900'}`}>{displayName}</p>
              </div>
              <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${showProfileMenu ? 'rotate-90' : ''} ${isGlobalDarkMode ? 'text-sidebar-foreground/40' : 'text-gray-400'}`} />
            </button>

            {/* Profile dropdown */}
            {showProfileMenu && (
              <div
                ref={profileMenuRef}
                className={`absolute bottom-full left-0 right-0 mb-2 rounded-xl border shadow-2xl overflow-hidden ${
                  isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
                }`}
              >
                {/* User info */}
                <div className={`p-3 border-b ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'}`}>
                  <button
                    onClick={() => { navigate('?modal=settings'); setShowProfileMenu(false); }}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all group ${isGlobalDarkMode ? 'hover:bg-sidebar' : 'hover:bg-gray-50'}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-gray-100'}`}>
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`text-sm font-bold ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-700'}`}>{initials}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className={`text-sm font-semibold truncate ${isGlobalDarkMode ? 'text-white' : 'text-gray-900'}`}>{displayName}</p>
                      <p className={`text-xs truncate ${isGlobalDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{profile?.email}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </button>
                </div>

                {/* Menu items */}
                <div className="p-2 space-y-0.5">
                  <button
                    onClick={() => { navigate('?modal=settings'); setShowProfileMenu(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      isGlobalDarkMode ? 'text-sidebar-foreground/80 hover:bg-sidebar' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    <span className="font-medium">Account Settings</span>
                  </button>

                  {/* Theme toggle */}
                  <div className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    isGlobalDarkMode ? 'text-sidebar-foreground/80' : 'text-gray-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <Monitor className="w-4 h-4" />
                      <span className="font-medium">Theme</span>
                    </div>
                    <button
                      aria-label="Toggle theme"
                      onClick={() => {
                        const next = !isGlobalDarkMode;
                        setIsGlobalDarkMode(next);
                        safeLocalStorage.setItem('themePreference', next ? 'dark' : 'light');
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        isGlobalDarkMode ? 'bg-indigo-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`pointer-events-none inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isGlobalDarkMode ? 'translate-x-5' : 'translate-x-0'
                      }`}>
                        {isGlobalDarkMode
                          ? <Moon className="w-3 h-3 text-indigo-600" />
                          : <Sun className="w-3 h-3 text-amber-500" />}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Logout */}
                <div className={`p-2 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'}`}>
                  <button
                    onClick={handleSignOut}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      isGlobalDarkMode ? 'text-sidebar-foreground/80 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium">Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      <main className={`flex-1 flex flex-col min-w-0 ${location.pathname.startsWith('/inbox') ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
        {children || <Outlet context={{ isGlobalDarkMode, setIsGlobalDarkMode }} />}
      </main>
    </div>
  );
};
